import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin } from 'rxjs';
import { HttpService } from 'src/app/services/http.service';
import { LanguageService } from 'src/app/services/language.service';
import { SettingsService } from 'src/app/services/settings.service';
import { DetailsViewMode } from '../../details-view-enum';
import { BikeComponent } from '../../component/component';
import { ConfirmationDialogData } from '../../modals/confirmation-dialog-data';
import { ConfirmationDialogComponent } from '../../modals/confirmation-dialog/confirmation-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AccountService } from 'src/app/services/account.service';
import { DataParserService } from 'src/app/services/data-parser.service';

/** Configuration details form for adding and editing Configurations. 
 * Configurations fields: Config name, Config ID, Last change date, Last changed by, OE, Brand, Comment,
 * Bike model name, Wheel circumference, Date of next service, ODO of next service, Light reserved area, Lower battery discharge limit, Max. szpport speed,
 * Default language, Default units, Keyless Go, Default support mode, Bike type, Upper battery charge limit,
 * Gear Sensor available, Left removte available, Center display available, Auto ligt enabled, Flex Battery, Tonewheel,
 * EoL Software Update Priority, Active Support modes, Manual checks for EoL operator.*/

@Component({
  selector: 'app-configuration-details',
  templateUrl: './configuration-details.component.html',
  styleUrls: ['./configuration-details.component.scss'],
  standalone: false
})
export class ConfigurationDetailsComponent implements OnInit {

  /** Defining add or edit configuration */
  public mode: DetailsViewMode = DetailsViewMode.New;
  public configurationId: number = -1;
  public configurationBikeCount: number = 0;
  public loading: boolean = false;
  public configNameError: boolean = false;
  public sameConfigError: boolean = false;
  private lastBikeModelName: string = "";
  private lastConfigName: string = "";
  public creationDate: Date | null = null;
  public createdBy: string = "";
  private skipConfirmation: boolean = false;

  public bikeTypeOptionsList: { id: number, name: string }[] = [];
  public oeOptionsList: { id: number, name: string }[] = [];
  public bootLogoIdList: { id: number, name: string }[] = [];
  private oeOptionsListAll: { id: number, name: string }[] = [];
  public brandOptionsList: { id: number, name: string, OEM_ID: number }[] = [];
  public componentClassOptionsList: { id: number, name: string }[] = [];
  public displayOrientationOptionsList: { id: number, name: string }[] = [
    { id: 1, name: this.languageService.languageJSON.ConfigurationDetails_DisplayOrientationNormal },
    { id: 2, name: this.languageService.languageJSON.ConfigurationDetails_DisplayOrientationUpsideDown }
  ];

  public defaultHMI = "Core HMI Aurix";
  public defaultCM = "Core HMI BLE";
  public componentList: BikeComponent[] = [];
  public batteryList: BikeComponent[] = [];
  public driveUnitList: BikeComponent[] = [];
  public remoteLeftList: BikeComponent[] = [];
  public remoteRightList: BikeComponent[] = [];
  public displayList: BikeComponent[] = [];
  public filteredBrandList: { id: number, name: string }[] = [];

  public configurationModifyRole: boolean = false;

  constructor(private fb: FormBuilder, public languageService: LanguageService,
    private httpService: HttpService, private router: Router, private route: ActivatedRoute,
    public settingsService: SettingsService, private matDialog: MatDialog, private snackBar: MatSnackBar,
    private accountService: AccountService, private dataParserService: DataParserService) { }

  /** Configuration FormGroup definition */
  public configurationGroup: FormGroup = this.fb.group({
    configName: ['', [
      Validators.required,
      Validators.pattern(/^(\s+\S+\s*)*(?!\s).*$/),
      Validators.maxLength(this.settingsService.getTitleMaxLength())
    ]],
    comment: ['', [
      Validators.maxLength(this.settingsService.getTitleMaxLength())
    ]],
    oe: ['', [
      Validators.required
    ]],
    brand: ['', [
      Validators.required
    ]],
    bikeType: ['', [
      Validators.required
    ]],
    bikeModelName: ['', [
      Validators.required,
      Validators.maxLength(this.settingsService.getTitleMaxLength()),
      Validators.pattern(/^(\s+\S+\s*)*(?!\s).*$/)
    ]],
    coreHMIAurix: [''],
    coreHMIAurixVariant: ['', [
      Validators.required
    ]],
    coreHMIBle: [''],
    coreHMIBleVariant: ['', [
      Validators.required
    ]],
    battery: ['', [
      Validators.required
    ]],
    batteryVariant: ['', [
      Validators.required
    ]],
    batteryAddOns: this.fb.array([
    ]),
    driveUnit: ['', [
      Validators.required
    ]],
    driveUnitVariant: ['', [
      Validators.required
    ]],
    remoteLeft: ['', [
    ]],
    remoteLeftVariant: [''],
    remoteRight: ['', [
    ]],
    remoteRightVariant: [''],
    display: ['', [
    ]],
    displayVariant: [''],
    wheelCircumference: ['', [
      Validators.required,
      Validators.min(750),
      Validators.max(2450)
    ]],
    lightReservedArea: ['', [
      Validators.required,
      Validators.min(0),
      Validators.max(20)
    ]],
    displayOrientation: ['', [
    ]],
    bootLogoId: ['', [
    ]]
  });
  private initialFormValues: any;

  get configName() {
    return this.configurationGroup.get("configName");
  }

  get comment() {
    return this.configurationGroup.get("comment");
  }

  get oe() {
    return this.configurationGroup.get("oe");
  }

  get brand() {
    return this.configurationGroup.get("brand");
  }

  get bikeModelName() {
    return this.configurationGroup.get("bikeModelName");
  }

  get bikeType() {
    return this.configurationGroup.get("bikeType");
  }

  get coreHMIAurix() {
    return this.configurationGroup.get("coreHMIAurix");
  }

  get coreHMIAurixVariant() {
    return this.configurationGroup.get("coreHMIAurixVariant");
  }

  get coreHMIBle() {
    return this.configurationGroup.get("coreHMIBle");
  }

  get coreHMIBleVariant() {
    return this.configurationGroup.get("coreHMIBleVariant");
  }

  get battery() {
    return this.configurationGroup.get("battery");
  }

  get batteryVariant() {
    return this.configurationGroup.get("batteryVariant");
  }

  get batteryAddOns(): FormArray {
    return this.configurationGroup.get("batteryAddOns") as FormArray;
  }

  get driveUnit() {
    return this.configurationGroup.get("driveUnit");
  }

  get driveUnitVariant() {
    return this.configurationGroup.get("driveUnitVariant");
  }

  get remoteLeft() {
    return this.configurationGroup.get("remoteLeft");
  }

  get remoteLeftVariant() {
    return this.configurationGroup.get("remoteLeftVariant");
  }

  get remoteRight() {
    return this.configurationGroup.get("remoteRight");
  }

  get remoteRightVariant() {
    return this.configurationGroup.get("remoteRightVariant");
  }

  get display() {
    return this.configurationGroup.get("display");
  }

  get displayVariant() {
    return this.configurationGroup.get("displayVariant");
  }

  get wheelCircumference() {
    return this.configurationGroup.get("wheelCircumference");
  }

  get lightReservedArea() {
    return this.configurationGroup.get("lightReservedArea");
  }

  get displayOrientation() {
    return this.configurationGroup.get("displayOrientation");
  }

  get bootLogoId() {
    return this.configurationGroup.get("bootLogoId");
  }

  /** Getting options lists, loading editable configuration details */
  ngOnInit(): void {
    this.configurationModifyRole = this.accountService.hasConfigurationModifyRole();
    this.loading = true;
    this.initialFormValues = this.configurationGroup.value;
    forkJoin([
      this.httpService.getBikeTypeOptionsList(),
      document.URL.includes("edit-configuration") || document.URL.includes("new-configuration") ? this.httpService.getOEOptionsList(0, 0) : this.httpService.getOEOptionsList(1, 0),
      this.httpService.getBrandOptionsList(),
      this.httpService.getComponentClassOptionsList(),
      this.httpService.getComponentList(),
      this.httpService.getOEOptionsList(1, 1)
    ]).subscribe((resp) => {
      this.bikeTypeOptionsList = resp[0];
      this.oeOptionsListAll = resp[5];
      this.oeOptionsList = resp[1];
      this.brandOptionsList = resp[2];
      this.componentClassOptionsList = resp[3];

      if ((resp[4] as any).success) {
        this.componentList = this.dataParserService.initComponents(resp[4], this.oeOptionsListAll, this.componentClassOptionsList);
        this.coreHMIAurix?.setValue(this.componentList[0]);
        this.coreHMIBle?.setValue(this.componentList[1]);
      }

      this.bootLogoIdList = resp[5];

      if (document.URL.includes("view-configuration")) {
        this.mode = DetailsViewMode.View;
      }
      if (document.URL.includes("edit-configuration")) {
        this.mode = DetailsViewMode.Edit;
      }

      if (document.URL.includes("edit-configuration") || document.URL.includes("view-configuration")) {
        this.route.queryParams
          .subscribe(params => {
            if (params['id'] == undefined) {
              this.goBack();
              return;
            }

            this.httpService.getConfigurationDetails(params['id']).subscribe((resp: any) => {
              this.loading = false;
              if (resp.success) {
                const configurationElement = resp.data;
                this.lastBikeModelName = configurationElement.bike_model_name;
                this.lastConfigName = configurationElement.config_name;
                this.configurationId = configurationElement.id;
                this.configurationBikeCount = configurationElement.bikes_count;
                this.oe?.setValue(configurationElement.oe);
                this.initComponentLists();
                const components = this.getComponents(configurationElement.component_variants);

                this.configurationGroup.setValue({
                  configName: configurationElement.config_name,
                  oe: configurationElement.oe,
                  brand: configurationElement.brand_id,
                  comment: configurationElement.comment,
                  bikeModelName: configurationElement.bike_model_name,
                  bikeType: configurationElement.motor_configuration_id,
                  coreHMIAurix: this.componentList[0],
                  coreHMIAurixVariant: components.coreHMIAurixVariantId,
                  coreHMIBle: this.componentList[1],
                  coreHMIBleVariant: components.coreHMIBleVariantId,
                  battery: components.battery,
                  batteryVariant: components.batteryVariantId,
                  batteryAddOns: [],
                  driveUnit: components.driveUnit,
                  driveUnitVariant: components.driveUnitVariantId,
                  remoteLeft: components.remoteLeft ? components.remoteLeft : "",
                  remoteLeftVariant: components.remoteLeftVariantId ? components.remoteLeftVariantId : "",
                  remoteRight: components.remoteRight ? components.remoteRight : "",
                  remoteRightVariant: components.remoteRightVariantId ? components.remoteRightVariantId : "",
                  display: components.display ? components.display : "",
                  displayVariant: components.displayVariantId ? components.displayVariantId : "",
                  wheelCircumference: configurationElement.wheel_circumference,
                  lightReservedArea: configurationElement.light_reserved_area,
                  displayOrientation: configurationElement.display_orientation,
                  bootLogoId: configurationElement.boot_logo_id
                });
                components.batteryAddOn?.forEach((el) => {
                  this.addBatteryAddOnFormGroup(el.battery, el.batteryVariant);
                });
                this.creationDate = configurationElement.created_at;
                this.createdBy = configurationElement.created_by;
                this.initialFormValues = this.configurationGroup.value;
              }
            }, (error) => {
              this.loading = false;
            });
          });
      }
      else {
        this.loading = false;
      }
    });
  }

  /** Sending new or edited configuration fromgroup data to Api endpoint */
  onSubmit(): void {
    console.log(this.configurationGroup);
    if (this.mode == DetailsViewMode.Edit && !this.isFormChanged()) {
      this.snackBar.open(this.languageService.languageJSON.Global_NoChangesMade, undefined, { panelClass: 'success-snackbar', duration: 5000 });
      this.goBack();

      return;
    }

    this.configurationGroup.markAllAsTouched();

    if (this.configurationGroup.status == "VALID") {
      this.loading = true;
      if (this.mode == DetailsViewMode.Edit) {
        const postData: any = {
          id: this.configurationId,
          oe: this.configurationGroup.value.oe,
          brand: this.configurationGroup.value.brand,
          comment: this.configurationGroup.value.comment,
          bike_type: this.configurationGroup.value.bikeType,
          component_data: this.getComponentData(),
          component_variants: this.getComponentVariants(),
          opt_component_variants: this.getComponentAddOnVariants(),
          config_name: this.configurationGroup.value.configName,
          bike_model_name: this.configurationGroup.value.bikeModelName,
          wheel_circumference: this.configurationGroup.value.wheelCircumference,
          light_reserved_area: this.configurationGroup.value.lightReservedArea,
          display_orientation: this.configurationGroup.value.display != -1 ? this.configurationGroup.value.displayOrientation : null,
          boot_logo_id: this.configurationGroup.value.display != -1 ? this.configurationGroup.value.bootLogoId : null
        }

        if (this.configurationGroup.value.configName != this.lastConfigName) {
          postData.config_name = this.configurationGroup.value.configName;
        }

        if (this.configurationGroup.value.bikeModelName != this.lastBikeModelName) {
          postData.bike_model_name = this.configurationGroup.value.bikeModelName;
        }

        this.httpService.editConfiguration(postData).subscribe((resp: any) => {
          this.loading = false;
          if (resp?.success) {
            this.skipConfirmation = true;
            this.snackBar.open(this.languageService.languageJSON.ConfigurationDetails_EditSuccessMessage, undefined, { panelClass: 'success-snackbar', duration: 5000 });
            this.router.navigate(['/configurations']);
          }
          else {
            this.onError(resp, "edit");
          }
        }, (error) => {
          this.loading = false;
        });
      }
      else {
        this.httpService.addConfiguration({
          config_name: this.configurationGroup.value.configName,
          oe: this.configurationGroup.value.oe,
          brand: this.configurationGroup.value.brand,
          comment: this.configurationGroup.value.comment,
          bike_model_name: this.configurationGroup.value.bikeModelName,
          bike_type: this.configurationGroup.value.bikeType,
          component_data: this.getComponentData(),
          component_variants: this.getComponentVariants(),
          opt_component_variants: this.getComponentAddOnVariants(),
          wheel_circumference: this.configurationGroup.value.wheelCircumference,
          light_reserved_area: this.configurationGroup.value.lightReservedArea,
          display_orientation: this.configurationGroup.value.display != -1 ? this.configurationGroup.value.displayOrientation : null,
          boot_logo_id: this.configurationGroup.value.display != -1 ? this.configurationGroup.value.bootLogoId : null
        }).subscribe({
          next: (resp: any) => {
            this.loading = false;
            if (resp?.success) {
              this.skipConfirmation = true;
              this.snackBar.open(this.languageService.languageJSON.ConfigurationDetails_AddSuccessMessage, undefined, { panelClass: 'success-snackbar', duration: 5000 });
              this.router.navigate(['/configurations']);
            }
            else {
              this.onError(resp, "add");
            }
          },
          error: () => {
            this.loading = false;
          }
        });
      }
    }
  }

  public getBatteryAddOnBikes(batteryAddOn: AbstractControl<any, any>) {
    return batteryAddOn.get("battery")?.value?.Variants;
  }

  private onError(resp: any, type: string) {
    if (resp?.failed?.code == "E015") {
      this.configNameError = true;
      document.getElementById("content-wrapper")?.scrollTo(0, 0);
    }
    else if (resp?.failed?.code == "E018") {
      this.sameConfigError = true;
      setTimeout(() => {
        document.getElementById("content-wrapper")?.scrollTo(0, document.body.scrollHeight);
      });
    }
    else {
      const message = type == "add" ? this.languageService.languageJSON.ConfigurationDetails_AddFailedMessage :
        this.languageService.languageJSON.ConfigurationDetails_EditFailedMessage;

      this.snackBar.open(message, undefined, { panelClass: 'success-snackbar', duration: 5000 });
    }
  }

  /** Opens edit configuration page */
  public editConfiguration() {
    this.router.navigate(['/edit-configuration'], { queryParams: { id: this.configurationId } });
  }

  /** Calling delete configuration Api endpoint */
  public deleteConfiguration() {
    const data: ConfirmationDialogData = {
      title: this.languageService.languageJSON.ComponentList_DeleteConfiguration_Title,
      message: this.languageService.languageJSON.Global_Delete_MessageWithName.replace("$name", `<b>${this.configName?.value}</b>`),
      buttons: [this.languageService.languageJSON.Global_Delete, this.languageService.languageJSON.Global_Cancel]
    };

    this.matDialog.open(ConfirmationDialogComponent, { data: { data } }).afterClosed().subscribe((result) => {
      if (result) {
        this.httpService.deleteConfiguration(this.configurationId).subscribe((resp: any) => {
          if (resp?.success) {
            this.skipConfirmation = true;
            this.snackBar.open(this.languageService.languageJSON.ConfigurationDetails_DeletedMessage, undefined, { panelClass: 'success-snackbar', duration: 5000 });
            this.router.navigate(['/configurations']);
          }
          else {
            this.snackBar.open(this.languageService.languageJSON.ConfigurationDetails_DeleteFailedMessage, undefined, { panelClass: 'success-snackbar', duration: 5000 });
          }
        });
      }
    });
  }

  public addBatteryAddOnFormGroup(battery?: BikeComponent, batteryVariant?: number) {
    const group = this.fb.group({
      battery: [battery ? battery : null, Validators.required],
      batteryVariant: [batteryVariant ? batteryVariant : null, Validators.required]
    });
    this.batteryAddOns.push(group);
  }

  private getComponents(componentVariants: any): {
    coreHMIAurixVariantId?: number,
    coreHMIBleVariantId?: number,
    battery?: BikeComponent,
    batteryVariantId?: number,
    batteryAddOn?: { battery?: BikeComponent, batteryVariant?: number }[],
    driveUnit?: BikeComponent,
    driveUnitVariantId?: number,
    remoteLeft?: BikeComponent,
    remoteLeftVariantId?: number,
    remoteRight?: BikeComponent,
    remoteRightVariantId?: number,
    display?: BikeComponent,
    displayVariantId?: number
  } {
    const components: {
      coreHMIAurixVariantId?: number,
      coreHMIBleVariantId?: number,
      battery?: BikeComponent,
      batteryVariantId?: number,
      batteryAddOn?: { battery?: BikeComponent, batteryVariant?: number }[],
      driveUnit?: BikeComponent,
      driveUnitVariantId?: number,
      remoteLeft?: BikeComponent,
      remoteLeftVariantId?: number,
      remoteRight?: BikeComponent,
      remoteRightVariantId?: number,
      display?: BikeComponent,
      displayVariantId?: number
    } = {};

    components.batteryAddOn = [];

    componentVariants.forEach((variant: any) => {
      if (variant.component.is_sub_component == 0) {
        if (variant.component.component_class == 1) {
          if (variant.component.component_identifier == "1000") {
            components.coreHMIAurixVariantId = variant.id;
          }
          else if (variant.component.component_identifier == "1001") {
            components.coreHMIBleVariantId = variant.id;
          }
        }
        else if (variant.component.component_class == 2) {
          components.driveUnit = this.driveUnitList.find((el) => { return el.Id == variant.component.id });
          components.driveUnitVariantId = variant.id;
        }
        else if (variant.component.component_class == 3 && variant.pivot.optional == 0) {
          components.battery = this.batteryList.find((el) => { return el.Id == variant.component.id });
          components.batteryVariantId = variant.id;
        }
        else if (variant.component.component_class == 3 && variant.pivot.optional == 1 && components.batteryAddOn) {
          components.batteryAddOn.push({
            battery: this.batteryList.find((el) => { return el.Id == variant.component.id }),
            batteryVariant: variant.id
          });
        }
        else if (variant.component.component_class == 4) {
          components.display = this.displayList.find((el) => { return el.Id == variant.component.id && el.AddOn == variant.pivot.optional });
          components.displayVariantId = variant.id;
        }
        else if (variant.component.component_class == 5 && variant.component.slot == 6) {
          components.remoteLeft = this.remoteLeftList.find((el) => { return el.Id == variant.component.id && el.AddOn == variant.pivot.optional });
          components.remoteLeftVariantId = variant.id;
        }
        else if (variant.component.component_class == 5 && variant.component.slot == 7) {
          components.remoteRight = this.remoteRightList.find((el) => { return el.Id == variant.component.id && el.AddOn == variant.pivot.optional });
          components.remoteRightVariantId = variant.id;
        }
      }
    });

    return components;
  }

  private getComponentData(): { id: number }[] {
    let componentData = [
      { id: this.coreHMIAurix?.value.ComponentIdentifier },
      { id: this.coreHMIBle?.value.ComponentIdentifier },
      { id: this.battery?.value.ComponentIdentifier },
      { id: this.driveUnit?.value.ComponentIdentifier }
    ];
    if (this.remoteLeft?.value && this.remoteLeft?.value.Id != -1) {
      componentData.push({ id: this.remoteLeft?.value.ComponentIdentifier });
    }
    if (this.remoteRight?.value && this.remoteRight?.value.Id != -1) {
      componentData.push({ id: this.remoteRight?.value.ComponentIdentifier });
    }
    if (this.display?.value && this.display?.value.Id != -1) {
      componentData.push({ id: this.display?.value.ComponentIdentifier });
    }

    return componentData;
  }

  public getComponentVariants(): { id: number }[] {
    let componentData = [
      { id: this.coreHMIAurixVariant?.value },
      { id: this.coreHMIBleVariant?.value },
      { id: this.batteryVariant?.value },
      { id: this.driveUnitVariant?.value }
    ];
    if (this.remoteLeft?.value && this.remoteLeft?.value.Id != -1 && this.remoteLeft?.value.AddOn != true) {
      componentData.push({ id: this.remoteLeftVariant?.value });
    }
    if (this.remoteRight?.value && this.remoteRight?.value.Id != -1 && this.remoteRight?.value.AddOn != true) {
      componentData.push({ id: this.remoteRightVariant?.value });
    }
    if (this.display?.value && this.display?.value.Id != -1 && this.display?.value.AddOn != true) {
      componentData.push({ id: this.displayVariant?.value });
    }

    return componentData;
  }

  public getComponentAddOnVariants(): { id: number }[] {
    let componentData: { id: number }[] = [];
    if (this.batteryAddOns?.controls) {
      componentData = componentData.concat(this.batteryAddOns?.value.map((el: any) => {
        return { id: el.batteryVariant };
      }));
    }
    if (this.remoteLeft?.value && this.remoteLeft?.value.Id != -1 && this.remoteLeft?.value.AddOn) {
      componentData.push({ id: this.remoteLeftVariant?.value });
    }
    if (this.remoteRight?.value && this.remoteRight?.value.Id != -1 && this.remoteRight?.value.AddOn) {
      componentData.push({ id: this.remoteRightVariant?.value });
    }
    if (this.display?.value && this.display?.value.Id != -1 && this.display?.value.AddOn) {
      componentData.push({ id: this.displayVariant?.value });
    }

    return componentData;
  }

  getAvailableBatteries(forIndex?: number) {
    // Get fixed battery
    const selectedFixedBattery = this.battery?.value;

    // Get dynamic batteries (excluding itself if forIndex is provided)
    const selectedDynamicBatteries = this.batteryAddOns.controls
      .map((group, index) => {
        if (forIndex !== undefined && index === forIndex) return null; // exclude self
        return group.get('battery')?.value;
      })
      .filter(b => b !== null);

    // Build exclusion list
    const exclusionList = [...selectedDynamicBatteries];
    if (selectedFixedBattery && forIndex != undefined) {
      exclusionList.push(selectedFixedBattery);
    }

    // Filter battery list
    return this.batteryList.filter(b => !exclusionList.includes(b));
  }

  removeSelector(index: number) {
    this.batteryAddOns.removeAt(index);
  }

  /** Configuration name key pressed detection for same name error change */
  public onConfigNameKeyPressed(): void {
    this.configNameError = false;
  }

  public sameConfigChanged(): void {
    this.sameConfigError = false;
  }

  public remoteLeftChanged(): void {
    if (this.remoteLeft?.value.Id == -1) {
      this.remoteLeftVariant?.clearValidators();
    }
    else {
      this.remoteLeftVariant?.setValidators([Validators.required]);
    }

    this.remoteLeftVariant?.updateValueAndValidity();
  }

  public remoteRightChanged(): void {
    if (this.remoteRight?.value.Id == -1) {
      this.remoteRightVariant?.clearValidators();
    }
    else {
      this.remoteRightVariant?.setValidators([Validators.required]);
    }

    this.remoteRightVariant?.updateValueAndValidity();
  }

  public displayChanged(): void {
    if (this.display?.value.Id == -1) {
      this.bootLogoId?.clearValidators();
      this.displayOrientation?.clearValidators();
      this.displayVariant?.clearValidators();
    }
    else {
      this.bootLogoId?.setValidators([Validators.required]);
      this.displayOrientation?.setValidators([Validators.required]);
      this.displayVariant?.setValidators([Validators.required]);
    }

    this.bootLogoId?.updateValueAndValidity();
    this.displayOrientation?.updateValueAndValidity();
  }

  public onOEMChanged() {
    this.initComponentLists();
    this.configurationGroup.setValue({
      configName: this.configName?.value,
      comment: this.comment?.value,
      oe: this.oe?.value,
      brand: "",
      bikeModelName: this.bikeModelName?.value,
      bikeType: this.bikeType?.value,
      coreHMIAurix: this.componentList[0],
      coreHMIAurixVariant: "",
      coreHMIBle: this.componentList[1],
      coreHMIBleVariant: "",
      battery: "",
      batteryVariant: "",
      batteryAddOns: [],
      driveUnit: "",
      driveUnitVariant: "",
      remoteLeft: "",
      remoteLeftVariant: "",
      remoteRight: "",
      remoteRightVariant: "",
      display: "",
      displayVariant: "",
      wheelCircumference: "",
      lightReservedArea: "",
      displayOrientation: "",
      bootLogoId: ""
    });
  }

  public initComponentLists() {
    this.batteryList = this.filterVariantForOEM(this.componentList.filter((el) => { return el.ComponentClass == 3 }));
    this.driveUnitList = this.filterVariantForOEM(this.componentList.filter((el) => { return el.ComponentClass == 2 }));
    this.remoteLeftList = this.filterVariantForOEM(this.componentList.filter((el) => { return el.ComponentClass == 5 && el.Slot == 6 }));
    this.remoteLeftList = this.createComponentListWithAddOns(this.remoteLeftList);
    this.remoteLeftList.unshift({
      Id: -1,
      OEMId: -1,
      Component: "",
      ComponentName: "No remote",
      ComponentClass: -1,
      Slot: -1,
      ComponentIdentifier: "",
      SubcomponentIdentifier: -1,
      Variants: []
    });
    this.remoteRightList = this.filterVariantForOEM(this.componentList.filter((el) => { return el.ComponentClass == 5 && el.Slot == 7 }));
    this.remoteRightList = this.createComponentListWithAddOns(this.remoteRightList);
    this.remoteRightList.unshift({
      Id: -1,
      OEMId: -1,
      Component: "",
      ComponentName: "No remote",
      ComponentClass: -1,
      Slot: -1,
      ComponentIdentifier: "",
      SubcomponentIdentifier: -1,
      Variants: []
    });
    this.displayList = this.filterVariantForOEM(this.componentList.filter((el) => { return el.ComponentClass == 4 }).filter((el) => {
      return !el.ComponentName.includes("External");
    }));
    this.displayList = this.createComponentListWithAddOns(this.displayList);
    this.displayList.unshift({
      Id: -1,
      OEMId: -1,
      Component: "",
      ComponentName: "No display",
      ComponentClass: -1,
      Slot: -1,
      ComponentIdentifier: "",
      SubcomponentIdentifier: -1,
      Variants: []
    });

    this.filteredBrandList = this.brandOptionsList.filter((el) => {
      return el.OEM_ID == this.oe?.value;
    });
  }

  filterVariantForOEM(bikeComponents: BikeComponent[]): BikeComponent[] {
    return bikeComponents.map((bikeComponent) => {
      bikeComponent.Variants = bikeComponent.Variants.filter((el) => { return this.isZfTenantSelected() || el.OEMId == this.oe?.value || el.Shared });
      return bikeComponent;
    });
  }

  clearVariant(variantType: string) {
    this.configurationGroup.get(variantType)?.setValue("");
  }

  clearAddOnVariant(batteryAddOn: AbstractControl<any, any>, variantType: string) {
    batteryAddOn.get(variantType)?.setValue("");
  }

  public viewBikes() {
    this.router.navigate(['/bike-overview'], { queryParams: { configurationId: this.configurationId } });
  }

  private getZFBrandId(): number {
    return this.brandOptionsList.find((el) => { return el.name == "ZF" })?.OEM_ID || 2;
  }

  private isZfTenantSelected(): boolean {
    return this.oe?.value == 2;
  }

  private createComponentListWithAddOns(componentList: BikeComponent[]): BikeComponent[] {
    return componentList.flatMap(item => [
      { ...item, ComponentName: `${item.ComponentName} (${this.languageService.languageJSON.Global_Mandatory})` },
      { ...item, ComponentName: `${item.ComponentName} (${this.languageService.languageJSON.Global_AddOn})`, AddOn: true }
    ]);
  }

  public goBack() {
    this.skipConfirmation = true;
    this.router.navigate(['/configurations']);
  }

  public isFormChanged(): boolean {
    const currentValues = this.configurationGroup.value;
    return JSON.stringify(currentValues) != JSON.stringify(this.initialFormValues);
  }
}